import { ProfessionalTypeEnum } from '@gql/graphql';
import type { StepLineStep } from '@presentation/components';
import { HeaderProfile, LoadingContainer, StepLine } from '@presentation/components';
import { BREAKPOINTS } from '@presentation/styles';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { useMemo } from 'react';
import { BookingPageAnimal } from './components/BookingPageAnimal';
import { BookingPageAuth } from './components/BookingPageAuth';
import { BookingPageGroomerForm } from './components/BookingPageGroomerForm';
import { BookingPageRecap } from './components/BookingPageRecap';
import { BookingPageSummary } from './components/BookingPageSummary';
import * as Styled from './styled';
import type { BookingPageProps } from './types';

export * from './types';

export const BookingPage: FC<BookingPageProps> = (props) => {
  const { currentStep, header, pro, animalsLoading = false } = props;
  const { translate } = useTranslation();
  const steps = useMemo((): StepLineStep[] => {
    const temp: StepLineStep[] = [
      { title: translate('booking.steps.auth') },
      { title: translate('booking.steps.info') },
    ];
    if (pro.type === ProfessionalTypeEnum.GROOMER) {
      temp.push({ title: translate('booking.steps.moreInfo') });
    }
    temp.push({ title: translate('booking.steps.summary') });
    return temp;
  }, [pro.type, translate]);

  const maxStep = useMemo(() => {
    if (pro.type === ProfessionalTypeEnum.GROOMER) return 3;
    return 2;
  }, [pro.type]);

  return (
    <>
      <Styled.WarningBanner>{translate('booking.warnings.notConfirmedYet')}</Styled.WarningBanner>
      <Styled.Wrapper>
        <HeaderProfile {...header}>
          <Styled.StepLineWrapper>
            <StepLine
              currentStep={currentStep}
              steps={steps}
              color="light"
              verticalBreakpoint={BREAKPOINTS.tablet}
            />
          </Styled.StepLineWrapper>
        </HeaderProfile>

        {currentStep < maxStep && (
          <Styled.CardContainer>
            {currentStep === 0 && <BookingPageAuth {...props} />}
            {currentStep === 1 && (
              <Styled.FormCardWrapper>
                {!!animalsLoading && <LoadingContainer />}
                {!animalsLoading && <BookingPageAnimal {...props} />}
              </Styled.FormCardWrapper>
            )}
            {currentStep === 2 && (
              <Styled.FormCardWrapper>
                <BookingPageGroomerForm {...props} />
              </Styled.FormCardWrapper>
            )}
            <Styled.RecapCardWrapper>
              <BookingPageRecap {...props} />
            </Styled.RecapCardWrapper>
          </Styled.CardContainer>
        )}

        {currentStep === maxStep && <BookingPageSummary {...props} />}
      </Styled.Wrapper>
    </>
  );
};
